import React, { Component, ReactNode, RefObject } from 'react';
import './ChatContainer.scss';
import { ChatMessage } from '../../models/ChatMessage';
import { ChatBalloon } from './ChatBalloon';
import Spinner from 'react-bootstrap/Spinner';
import Alert from 'react-bootstrap/Alert';
import { connect } from 'react-redux';
import { ApplicationState } from '../../app/store';
import { SingleUser } from '../../api/types';
import { EDossierGroupType } from '../../models/DossierGroup';

interface ChatContainerProps {
  user?: SingleUser;
  name?: string;
  infoMessage?: string;
  chatMessages: ChatMessage[];
  conversationType: EDossierGroupType;
  doNotDisplayUserNames?: boolean;
  loading: boolean;
  dossierId: number;
  error?: boolean;
  className?: string;
}

interface ChatContainerState {
  hasLoadedOnce: boolean;
}

export class ChatContainerComponent extends Component<ChatContainerProps, ChatContainerState> {
  state: ChatContainerState = {
    hasLoadedOnce: false,
  };

  private bottomRef: RefObject<HTMLDivElement>;
  private unseenRef: RefObject<HTMLDivElement>;

  constructor(props: ChatContainerProps) {
    super(props);
    this.bottomRef = React.createRef();
    this.unseenRef = React.createRef();
  }

  componentDidMount(): void {
    this.scrollOnFirstFinishedLoad();
  }

  componentDidUpdate(previousProps: ChatContainerProps): void {
    if (!this.props.loading && previousProps.chatMessages.length !== this.props.chatMessages.length) {
      this.scrollToLastMessages();
    }
  }

  private scrollOnFirstFinishedLoad(): void {
    const { loading } = this.props;
    const { hasLoadedOnce } = this.state;
    if (!loading && !hasLoadedOnce) {
      this.scrollToLastMessages();
      this.setState({ hasLoadedOnce: true });
    }
  }

  scrollToLastMessages(): void {
    if (this.unseenRef.current) {
      this.unseenRef.current.scrollIntoView({ block: 'center' });
    } else if (this.bottomRef.current) {
      this.bottomRef.current.scrollIntoView();
    } else {
      console.error(
        'Failed to scroll to bottom because of missing ref.',
        { bottomRef: !!this.bottomRef.current, unseenRef: !!this.unseenRef.current }
      );
    }
  }

  renderMessageBox(): JSX.Element {
    const { chatMessages, user, dossierId, name, infoMessage } = this.props;
    let unseenLineDrawn = false;
    let hasUnseenMessages = false;
    return (
      <div className="chat-container__message-box">
        {
          chatMessages.length === 0 && <div className="chat-container__empty-chat-message">Er zijn geen berichten{name ? <> in <strong>{name}</strong></> : ''}</div>
        }
        {
          infoMessage && <div className="chat-container__info-message">{infoMessage}</div>
        }
        {
          chatMessages.map((chatMessage: ChatMessage, index: number) => {
            if (!chatMessage.seen) {
              hasUnseenMessages = true;
            }

            const nextMessage = chatMessages[index + 1];
            const nextMessageHasSameTimeAndSender = (
              nextMessage &&
              nextMessage.sendBy.id === chatMessage.sendBy.id &&
              Math.floor(nextMessage.timestamp.getTime() / 60_000) === Math.floor(chatMessage.timestamp.getTime() / 60_000) // floor epoch milliseconds to minutes
            );

            const options: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'long', day: 'numeric' };
            const currentMessageDate = chatMessage.timestamp.toLocaleDateString('nl-NL', options);
            let printDate = true;
            if (index !== 0) {
              const previousMessageDate = chatMessages[index - 1].timestamp.toLocaleDateString('nl-NL', options);
              if (previousMessageDate === currentMessageDate) {
                printDate = false;
              }
            }

            const fromThisUser = chatMessage.sendByName === user?.name;
            const element = (
              <div
                className="chat-container__message-container"
                key={`${chatMessage.timestamp.toISOString()}_${chatMessage.id}`}
              >
                {
                  (hasUnseenMessages && !unseenLineDrawn) &&
                  <div className="chat-container__new" ref={this.unseenRef}>Ongelezen</div>
                }
                { printDate && <div className="chat-container__date">{currentMessageDate}</div>}
                <ChatBalloon
                  className={`chat-container__message chat-container__message--${fromThisUser ? 'sent' : 'received'}`}
                  chatMessage={chatMessage}
                  doNotDisplayUserNames={this.props.doNotDisplayUserNames}
                  hideTime={nextMessageHasSameTimeAndSender}
                  dossierId={dossierId}
                />
              </div>
            );

            if (hasUnseenMessages && !unseenLineDrawn) {
              unseenLineDrawn = true;
            }

            return element;
          })
        }
      </div>
    );
  }

  render(): ReactNode {
    const { className, loading, error } = this.props;
    const extraClassName = className ? className : '';
    return (
      <div className={`chat-container ${extraClassName}`}>
        {
          (!loading && !error) ?
            this.renderMessageBox() :
            <div className="chat-container__status">
              {loading && <Spinner animation="border" variant="primary" />}
              {error && <Alert variant="danger">Laden mislukt</Alert>}
            </div>
        }
        <div ref={this.bottomRef}></div>
      </div>
    );
  }
}

export const ChatContainer = connect((state: ApplicationState) => ({
  user: state.session.user,
}))(ChatContainerComponent);
